import React, { useState } from "react";
import Dialog from "Components/DesignSystem/Dialog/Dialog";
import Button from "Components/DesignSystem/Button/Button";
import Typography from "Components/DesignSystem/Typography/Typography";
import { toast } from "Components/DesignSystem/Toast/useToast";
import { Upload } from "Components/DesignSystem/Iconography/Icons";
import { validateDbmFile, uploadDbmFile, TDbmBulkActionResult, TDbmChoicesPayload } from "./dbmBulkActionService";
import { TDBMTick } from "../RuleComponents/dataTypes";

type TDBMBulkActionModalProps = {
  mode: "upload" | "validate";
  /** The field's current selectType + live choice lists — everything the
   * stateless backend needs, since there is no persisted rule to look this up
   * from (DBM is embedded like CBM, not a separate REST resource). */
  choicesPayload: TDbmChoicesPayload;
  isOpen: boolean;
  onClose: () => void;
  /** Upload only — applies the parsed ticks to the in-memory matrix. */
  onUploaded?: (ticks: TDBMTick[]) => void;
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Upload XLS / Validate XLS — same layout, controls and behaviour for both;
 * only the title and which service call runs differ. Referenced from the
 * Compatibility rule section's `PdwUploadDropzone` for structure and DS
 * `Button` usage, but NOT reused: that flow auto-closes and hides inline
 * errors (`hide_error`), which is the opposite of what this needs — the modal
 * must stay open showing a scrollable error list until the user acts, and
 * only toast+close on success. New drag-and-drop support here too — the
 * legacy dropzone is click-only.
 */
const DBMBulkActionModal = ({ mode, choicesPayload, isOpen, onClose, onUploaded }: TDBMBulkActionModalProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [inputKey, setInputKey] = useState("");

  const reset = () => {
    setSelectedFile(null);
    setErrors([]);
    setInputKey(Math.random().toString(36));
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setErrors([]);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) {
      setSelectedFile(file);
      setErrors([]);
    }
  };

  const handleDelete = () => {
    setSelectedFile(null);
    setErrors([]);
  };

  const handleSave = async () => {
    if (!selectedFile) return;
    setBusy(true);
    setErrors([]);
    const result: TDbmBulkActionResult =
      mode === "validate" ? await validateDbmFile(choicesPayload, selectedFile) : await uploadDbmFile(choicesPayload, selectedFile);
    setBusy(false);
    if (result.done) {
      // Toast fires AFTER the modal closes — success is confirmed once the user is
      // back on the panel, not while still looking at the file area.
      handleClose();
      toast.success(mode === "validate" ? "File is valid" : "File uploaded successfully");
      if (mode === "upload") onUploaded?.(result.ticks ?? []);
    } else {
      setErrors(result.errors?.length ? result.errors : [result.message ?? "The file could not be processed"]);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={busy ? () => {} : handleClose}
      closeOnOutsideClick={!busy}
      title={mode === "validate" ? "Validate XLS" : "Upload XLS"}
      data-testid={`rules-dbmbulkactionmodal-${mode}-dialog`}
    >
      <div>
        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          className={`tw-border tw-rounded-srf tw-p-8 motion-safe:tw-transition-colors ${
            isDragging ? "tw-border-accent tw-bg-accent-tint" : "tw-border-edge"
          }`}
          data-testid="rules-dbmbulkactionmodal-dropzone"
        >
          <label
            htmlFor={`dbm-bulk-action-${mode}-file`}
            className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-text-center tw-cursor-pointer tw-text-accent"
          >
            <input
              data-testid={`rules-dbmbulkactionmodal-${mode}-input`}
              type="file"
              accept=".xlsx"
              id={`dbm-bulk-action-${mode}-file`}
              className="tw-sr-only"
              key={inputKey}
              disabled={busy}
              onChange={handleFileSelect}
              onClick={(event) => ((event.target as HTMLInputElement).value = "")}
            />
            <Upload size={32} aria-hidden="true" />
            {selectedFile ? (
              /* A generated file name is one unbroken token, so `break-words` cannot wrap it —
                 it set the min-content width and ran past the dropzone (AB#15618). The name
                 ellipsizes inside a full-width row instead, full value in `title`; the size
                 never shrinks so it stays readable. */
              <div className="tw-mt-1 tw-flex tw-w-full tw-min-w-0 tw-justify-center tw-gap-1">
                <Typography
                  variant="body-primary"
                  as="span"
                  className="tw-text-ink tw-truncate tw-min-w-0"
                  title={selectedFile.name}
                  data-testid="rules-dbmbulkactionmodal-file-name"
                >
                  {selectedFile.name}
                </Typography>
                <Typography
                  variant="body-primary"
                  as="span"
                  className="tw-text-ink tw-shrink-0 tw-whitespace-nowrap"
                  data-testid="rules-dbmbulkactionmodal-file-size"
                >
                  ({formatFileSize(selectedFile.size)})
                </Typography>
              </div>
            ) : (
              <>
                <Typography variant="body-primary" as="span" className="tw-text-ink">
                  (Note: Only .xlsx files are allowed to upload)
                </Typography>
                <Typography className="tw-text-ink" variant="title-header" as="span">
                  Browse File
                </Typography>
              </>
            )}
          </label>
        </div>

        {errors.length > 0 && (
          <div
            className="tw-mt-4 tw-max-h-40 tw-overflow-y-auto tw-border tw-border-tone-danger-ink tw-bg-tone-danger-tint tw-rounded-srf tw-p-3"
            data-testid="rules-dbmbulkactionmodal-errors"
          >
            {errors.map((error, index) => (
              <Typography key={index} variant="body-primary" className="tw-text-tone-danger-ink">
                {error}
              </Typography>
            ))}
          </div>
        )}

        <div className="tw-mt-4 tw-flex tw-items-center tw-justify-between tw-gap-3">
          <Button
            variant="danger"
            data-testid="rules-dbmbulkactionmodal-delete-btn"
            disabled={!selectedFile || busy}
            onClick={handleDelete}
          >
            Delete
          </Button>
          <div className="tw-flex tw-items-center tw-gap-3">
            <Button variant="secondary" data-testid="rules-dbmbulkactionmodal-cancel-btn" disabled={busy} onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              data-testid="rules-dbmbulkactionmodal-save-btn"
              disabled={!selectedFile || busy}
              loading={busy}
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default DBMBulkActionModal;
